import { Assessment, CalendarToday, Download, Error } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Card, CardContent, Chip, CircularProgress, Typography } from "@mui/material";
import { Buffer } from "buffer";
import dayjs from "dayjs";
import saveAs from "file-saver";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import useFetch from "src/Components/Common/useFetch";
import CustomToast from "src/Components/CustomToast";
import ReactTable, { ColDef } from "src/Components/ReactTable/ReactTable";
import EmptyReconciliationImg from "src/Graphics/VendorPortal/Graphic.svg";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { filterDateBetweenStartAndEndDate } from "src/Utils/DateUtils";
import { trackReconSummaryViewed } from "src/Utils/Mixpanel/VendorPortalEvents";
import { formatMoney, ToDineroObj } from "src/Utils/MoneyUtils";
import { CustomButton } from "../../Common/Button/CustomButton";
import { LoadingPage } from "../PartnerCommunication/MsmePartnerPortal/PartnerPortal.common";
import VendorPortalFeedbackModal from "./VendorPortalFeedbackModal";
import { useVendorPortal } from "./VendorPortalLayout";

const REPORT_POLL_INTERVAL_MS = 5000;
const REPORT_POLL_MAX_ATTEMPTS = 36; // ~3 minutes

const ReconciliationVendorPortal = () => {
  const { handleUnauthorizedError, isSessionReady, token } = useVendorPortal();
  const [loader, setLoader] = useState(true);
  const [isPastReconciliation, setIsPastReconciliation] = useState(false);
  const [pastReconciliationData, setPastReconciliationData] = useState([]);
  const [taskListData, setTaskListData] = useState([]);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [isDownReconSummaryReport, setIsDownReconSummaryReport] = useState(false);

  useEffect(() => {
    // Only run when session is ready
    if (!isSessionReady) return;
    if (token) {
      // If token exists, use it to fetch data
      trackReconSummaryViewed();
      listReconSummaryOverview(token);
    } else {
      // If no token, show error
      toast.error(<CustomToast message="Session expired. Please log in again." />);
    }
  }, [isSessionReady, token]); // Depend on both isSessionReady and token

  const listReconSummaryOverview = (token) => {
    setLoader(true);
    useFetch(API_ENDPOINTS.VENDOR_PORTAL_RECON_SUMMARY_OVERVIEW.url, "GET", {
      failureMessage: API_ENDPOINTS.VENDOR_PORTAL_RECON_SUMMARY_OVERVIEW.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      thenCallBack: (response) => {
        setTaskListData(response.data?.tasksList || []);
        setPastReconciliationData([]);
        setLoader(false);
      },
      catchCallBack: (error) => {
        handleUnauthorizedError(error);
      },
    });
  };

  const downReconSummaryReport = (mappingId, attempt = 0) => {
    setIsDownReconSummaryReport(true);
    useFetch(API_ENDPOINTS.VENDOR_PORTAL_RECON_SUMMARY_DOWNLOAD.url, "POST", {
      failureMessage: API_ENDPOINTS.VENDOR_PORTAL_RECON_SUMMARY_DOWNLOAD.failureMessage,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
      data: {
        mappingId,
      },
      thenCallBack: (response) => {
        // The excel is generated on demand after a recon run; poll until it is ready
        if (response.data?.reportStatus === "GENERATING") {
          if (attempt === 0) {
            toast.info(<CustomToast message="Preparing your report, the download will start automatically." />);
          }
          if (attempt >= REPORT_POLL_MAX_ATTEMPTS) {
            toast.error(
              <CustomToast message="Report is taking longer than usual. Please try again in a few minutes." />
            );
            setIsDownReconSummaryReport(false);
            return;
          }
          setTimeout(() => downReconSummaryReport(mappingId, attempt + 1), REPORT_POLL_INTERVAL_MS);
          return;
        }
        const filename = `Recon_Summary_Report.xlsm`;
        const excelData = Buffer.from(response.data.reportData, "base64");
        const fileType = "application/vnd.ms-excel.sheet.macroEnabled.12";
        const blob = new Blob([excelData], { type: fileType });
        saveAs(blob, filename);
        setIsDownReconSummaryReport(false);
      },
      catchCallBack: () => {
        setIsDownReconSummaryReport(false);
      },
    });
  };

  const columnDefinitionReconciliationVendorPortal = React.useMemo(
    (): ColDef<any>[] => [
      {
        header: "Particulars",

        accessorFn: (row) => row.gapDescription || "",
        size: 280,
      },
      {
        header: "Annexures",
        accessorFn: (row) => row.annexures || "",
        size: 300,
      },

      {
        header: "Action Owner",
        accessorFn: (row) => row.actionOwner || "",
        size: 300,
      },
      {
        header: "Own Amount",

        accessorFn: (row) => row.ownAmount || "",
        size: 250,
        filterVariant: "range",
        filterFn: "betweenInclusive",
        Cell: ({ row }: any) => (
          <div
            className="textOverflow_hidden"
            title={row.original?.ownAmount ? formatMoney(ToDineroObj(row.original?.ownAmount)) : ""}
          >
            {row.original?.ownAmount ? formatMoney(ToDineroObj(row.original?.ownAmount)) : "-"}
          </div>
        ),
      },
      {
        header: "Partner Amount",

        accessorFn: (row) => row.partnerAmount || "",
        size: 250,
        filterVariant: "range",
        filterFn: "betweenInclusive",
        Cell: ({ row }: any) => (
          <div
            className="textOverflow_hidden"
            title={row.original?.partnerAmount ? formatMoney(ToDineroObj(row.original?.partnerAmount)) : ""}
          >
            {row.original?.partnerAmount ? formatMoney(ToDineroObj(row.original?.partnerAmount)) : "-"}
          </div>
        ),
      },
      {
        header: "Amount Difference",

        accessorFn: (row) => row.amountDifference || "",
        size: 250,
        filterVariant: "range",
        filterFn: "betweenInclusive",
        Cell: ({ row }: any) => (
          <div
            className="textOverflow_hidden"
            title={row.original?.amountDifference ? formatMoney(ToDineroObj(row.original?.amountDifference)) : ""}
          >
            {row.original?.amountDifference ? formatMoney(ToDineroObj(row.original?.amountDifference)) : "-"}
          </div>
        ),
      },
    ],
    []
  );

  const columnDefinitionPastReconciliation = React.useMemo(
    (): ColDef<any>[] => [
      {
        header: "Task ID",
        accessorFn: (row) => row.taskId || "",
        size: 180,
      },

      {
        header: "Recon Period Start",

        accessorFn: (row) => new Date(row.reconPeriodStart || null),
        size: 350,
        filterVariant: "date-range",
        // custom filter function
        filterFn: (row, _id, filterValue, _meta) => {
          const minDate = filterValue[0] ? dayjs(filterValue[0]).toDate() : null;
          const maxDate = filterValue[1] ? dayjs(filterValue[1]).toDate() : null;
          if (minDate || maxDate) {
            return filterDateBetweenStartAndEndDate(minDate, maxDate, row.original.reconPeriodStart);
          }
        },
        Cell: ({ row }) => (
          <div className="textOverflow_hidden">
            {row.original.reconPeriodStart ? moment(row.original.reconPeriodStart)?.format("DD-MM-YYYY") : "-"}
          </div>
        ),
      },
      {
        header: "Recon Period End",

        accessorFn: (row) => new Date(row.reconPeriodEnd || null),
        size: 350,
        filterVariant: "date-range",
        // custom filter function
        filterFn: (row, _id, filterValue, _meta) => {
          const minDate = filterValue[0] ? dayjs(filterValue[0]).toDate() : null;
          const maxDate = filterValue[1] ? dayjs(filterValue[1]).toDate() : null;
          if (minDate || maxDate) {
            return filterDateBetweenStartAndEndDate(minDate, maxDate, row.original.reconPeriodEnd);
          }
        },
        Cell: ({ row }) => (
          <div className="textOverflow_hidden">
            {row.original.reconPeriodEnd ? moment(row.original.reconPeriodEnd)?.format("DD-MM-YYYY") : "-"}
          </div>
        ),
      },
      {
        header: "Status",
        accessorFn: (row) => row.status || "", // Assuming `status` is the key in your data
        filterVariant: "multi-select",
        size: 250,
        Cell: ({ cell }) => {
          const reconStatus = cell.getValue() as string; // Get the status value
          let chipColor: "primary" | "warning" | "success" | "default" | "secondary" | "error" | "info" = "default"; // Default chip color
          if (reconStatus === "") chipColor = "primary";
          else if (reconStatus === "PendingApproval") chipColor = "warning";
          else if (reconStatus === "InvoiceProcessing") chipColor = "success";
          else if (reconStatus === "Posted") chipColor = "success";

          return <Chip label={reconStatus} size="small" color={chipColor} variant="outlined" />;
        },
      },

      {
        header: "Actions",
        size: 250,
        Cell: ({ row }) => (
          <Box>
            <CustomButton
              variant="outlined"
              size="small"
              colorToken="violet"
              onClick={() => {
                console.log("Download Report clicked for task ID:", row.original.taskId);
              }}
            >
              Download Report
            </CustomButton>
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <>
      {loader ? (
        <LoadingPage noHeader={true} />
      ) : (
        <>
          {taskListData.length > 0 ? (
            <Box sx={{ p: 3, backgroundColor: "#fff", minHeight: "100vh" }}>
              {!isPastReconciliation ? (
                <>
                  {taskListData.map((task) => (
                    <Box sx={{ mb: 5 }} key={task.taskId}>
                      {/* Header Cards */}
                      <Box sx={{ display: "flex", gap: 0, mb: 3 }}>
                        {/* Task ID Card */}
                        <Card sx={{ flex: 1, backgroundColor: "#fafafa", borderRadius: "8px 0 0 8px" }}>
                          <CardContent
                            sx={{
                              textAlign: "center",
                              py: 2,
                              "&:last-child": {
                                paddingBottom: "16px",
                              },
                            }}
                          >
                            <Box
                              sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}
                            >
                              <Assessment sx={{ fontSize: 16, color: "rgba(0,0,0,0.6)" }} />
                              <Typography className="heading-xs" sx={{ color: "rgba(0,0,0,0.6)" }}>
                                Task ID
                              </Typography>
                            </Box>
                            <Typography className="body-m-medium" sx={{ color: "rgba(0,0,0,0.87)" }}>
                              {task.taskId}
                            </Typography>
                          </CardContent>
                        </Card>

                        {/* Recon Status Card */}
                        <Card
                          sx={{
                            flex: 1,
                            backgroundColor: "#fafafa",
                            borderRadius: 0,
                            borderLeft: "1px solid rgba(0,0,0,0.01)",
                          }}
                        >
                          <CardContent
                            sx={{
                              textAlign: "center",
                              py: 2,
                              "&:last-child": {
                                paddingBottom: "16px",
                              },
                            }}
                          >
                            <Box
                              sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}
                            >
                              <Error sx={{ fontSize: 16, color: "rgba(0,0,0,0.6)" }} />
                              <Typography className="heading-xs" sx={{ color: "rgba(0,0,0,0.6)" }}>
                                Recon Status
                              </Typography>
                            </Box>
                            <Chip size="small" label={task.status} variant="outlined" color="warning" />
                          </CardContent>
                        </Card>

                        {/* Recon Period Card */}
                        <Card
                          sx={{
                            flex: 1,
                            backgroundColor: "#fafafa",
                            borderRadius: "0 8px 8px 0",
                            borderLeft: "1px solid rgba(0,0,0,0.01)",
                          }}
                        >
                          <CardContent
                            sx={{
                              textAlign: "center",
                              py: 2,
                              "&:last-child": {
                                paddingBottom: "16px",
                              },
                            }}
                          >
                            <Box
                              sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}
                            >
                              <CalendarToday sx={{ fontSize: 16, color: "rgba(0,0,0,0.6)" }} />
                              <Typography className="heading-xs" sx={{ color: "rgba(0,0,0,0.6)" }}>
                                Recon Period
                              </Typography>
                            </Box>
                            <Typography className="body-m-medium" sx={{ color: "rgba(0,0,0,0.87)" }}>
                              {task.period.startDate} - {task.period.endDate}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>

                      {/* Reconciliation Summary Table */}
                      <Card
                        sx={{
                          boxShadow:
                            "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
                        }}
                      >
                        <CardContent
                          sx={{
                            p: 0,
                            "&:last-child": {
                              paddingBottom: "0px !important",
                            },
                          }}
                        >
                          {/* Table Header */}
                          <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography className="heading-m" sx={{ color: "#000000" }}>
                                Reconciliation Summary
                              </Typography>
                              <CustomButton
                                variant="contained"
                                startIcon={isDownReconSummaryReport ? <CircularProgress size={20} /> : <Download />}
                                size="medium"
                                colorToken="violet"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: "none",
                                }}
                                onClick={() => {
                                  downReconSummaryReport(task.mappingId);
                                }}
                                disabled={
                                  isDownReconSummaryReport ||
                                  !task.reconciliationData ||
                                  task.reconciliationData?.length === 0
                                }
                              >
                                Download Reconciliation
                              </CustomButton>
                            </Box>
                          </Box>

                          {/* Table */}
                          <Box>
                            <ReactTable
                              columns={columnDefinitionReconciliationVendorPortal}
                              rows={task.reconciliationData || []}
                              loading={loader}
                              enableGrouping={true}
                              paginationDisplayMode={"default"}
                              enableRowSelection={false}
                              initialState={{
                                showColumnFilters: false,
                              }}
                              enablePagination={false}
                              renderBottomToolbarCustomActions={() => {
                                return <div> </div>;
                              }}
                              dynamicHeight={true} // Enable dynamic height
                              // enableToolbarInternalActions={false}
                              // enableTopToolbar={false}
                              // renderTopToolbar={() => null}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}

                  {/* Action Buttons Close and View Past Reconciliation */}
                  {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <CustomButton
          variant="outlined"
          size="medium"
          colorToken="violet"
          sx={{
            fontWeight: 600,
            textTransform: 'none'
          }}
        >
          Close Reconciliation
        </CustomButton>
        <CustomButton
          variant="outlined"
          size="medium"
          colorToken="violet"
          sx={{
            fontWeight: 600,
            textTransform: 'none'
          }}
          onClick={() => setIsPastReconciliation(true)}
        >
          View Past Reconciliation
        </CustomButton>
      </Box> */}
                </>
              ) : (
                <>
                  <Box>
                    <CustomButton
                      variant="outlined"
                      size="medium"
                      colorToken="violet"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => setIsPastReconciliation(false)}
                    >
                      Back
                    </CustomButton>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Card
                      sx={{
                        boxShadow:
                          "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        {/* Table Header */}
                        <Box sx={{ p: 2, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography className="heading-m" sx={{ color: "#000000" }}>
                              Past Reconciliations
                            </Typography>
                          </Box>
                        </Box>

                        {/* Table */}
                        <Box>
                          <ReactTable
                            columns={columnDefinitionPastReconciliation}
                            rows={pastReconciliationData}
                            loading={loader}
                            enableGrouping={true}
                            paginationDisplayMode={"default"}
                            enableRowSelection={false}
                            initialState={{
                              showColumnFilters: false,
                            }}
                            // enableToolbarInternalActions={false}
                            // enableTopToolbar={false}
                            // renderTopToolbar={() => null}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ pt: 4, backgroundColor: "#fff", minHeight: "100vh", display: "flex", justifyContent: "center" }}>
              <Box sx={{ textAlign: "center", maxWidth: "480px" }}>
                <img src={EmptyReconciliationImg} alt="Empty reconciliation" />
                <Typography
                  className="heading-m"
                  sx={{
                    color: "rgba(0, 0, 0, 0.87)",
                    fontSize: "24px",
                    lineHeight: "32px",
                    marginBottom: "8px",
                    fontWeight: 500,
                  }}
                >
                  There is no ongoing reconciliation
                </Typography>
                <Box>
                  <CustomButton variant="outlined" size="small" colorToken="violet">
                    Coming soon- AI-based Recon
                  </CustomButton>
                </Box>
                {/* <Box sx={{ mt: 1 }}>
                  <CustomButton
                    variant="contained"
                    size="medium"
                    colorToken="violet"
                    onClick={() => setOpenFeedback(true)}
                  >
                    Give Feedback
                  </CustomButton>
                </Box> */}
              </Box>
            </Box>
          )}
          {openFeedback && <VendorPortalFeedbackModal openFeedback={openFeedback} setOpenFeedback={setOpenFeedback} />}
        </>
      )}
    </>
  );
};

export default ReconciliationVendorPortal;
